import { axiosInstance } from './instance';
import { PaymentPlanType, PaymentPlanStatus, TokenAmountType } from './types/smithy';
import type { PolicySet } from './types/smithy';
import { PaymentStatus } from '@dev-zeromoblt/o-platform-client';
import type {
  SubscriptionV2Summary,
  GetSubscriptionV2Output,
  GetPaymentOutput,
  CreateSubscriptionV2Output,
  SubscriptionPreview,
  SubscriptionV2Index,
  ScheduledInstallment,
  IssuedToken,
} from '@dev-zeromoblt/o-platform-client';

// ─── Re-export Smithy enums ───────────────────────────────────────────────────
export { PaymentPlanType, TokenAmountType, PaymentStatus };

// ─── Re-export Smithy types used across the app ───────────────────────────────
// All subscription V2 types come directly from the generated client — no manual
// duplicates. Import from here rather than from @smithy/o-platform-client directly.
export type {
  // Enums
  SubscriptionV2Status,
  SubscriptionV2Index,
  InstallmentPaymentStatus,
  // Core subscription structures
  ScheduledInstallment,
  PaymentCalendar,
  IssuedToken,
  TokenCalculationBasis,
  TokenAmountResult,
  SubscriptionPricingDetails,
  SubscriptionShiftDetails,
  ShiftSchedule,
  // Summary / list / search shapes
  SubscriptionV2Summary,
  // Full detail shape
  GetSubscriptionV2Output,
  GetPaymentOutput,
  // Create / preview shapes
  CreateSubscriptionV2Output,
  SubscriptionPreview,
} from '@dev-zeromoblt/o-platform-client';

// ─── Legacy payment types ─────────────────────────────────────────────────────

export interface CreatePaymentInput {
  subscriberId: string;
  granteeId: string;
  email: string;
  phone: string;
  amount: string; // rupees as integer string e.g. "500"
  productInfo: string;
  paymentPlanId?: string;
  /** Student subscription start date (YYYY-MM-DD). */
  subscriptionStartDate?: string;
  /** LAM-150: Set for installment/renewal payments. */
  subscriptionV2Id?: string;
  /** LAM-150: Which installment number is being paid (1-based). */
  subscriptionV2InstallmentNumber?: number;
  /** Transaction ID (adjustment, discount, referral coupon) to apply to this payment — deprecated, use transactionIds */
  transactionId?: string;
  /** List of adjustment transaction IDs to apply to this payment (multi-adjustment support) */
  transactionIds?: string[];
  tags?: {
    plan?: string;
    distanceToCampus?: number;
  };
}

export interface PaymentFormData {
  key: string;
  txnid: string;
  amount: string;
  productinfo: string;
  firstname: string;
  email: string;
  phone: string;
  surl: string;
  furl: string;
  hash: string;
}

export interface CreatePaymentOutput {
  id: string;
  paymentUrl: string;
  paymentFormData: PaymentFormData;
  htmlForm: string;
}

export interface TokenAmount {
  required: boolean;
  value?: number;
}

export interface PartPaymentSchedule {
  sequenceNumber: number;
  durationDays: number;
  amountPercentage: number;
}

export interface PaymentSchedule {
  scheduleType: PaymentPlanType;
  customSchedule?: PartPaymentSchedule[];
}

// ─── Payment Plan (V3) ────────────────────────────────────────────────────────

export interface CreatePaymentPlanInput {
  termConfigId: string;
  pricingConfigId: string;
  planType: PaymentPlanType;
  discountPercentage?: number;
  tokenAmount?: TokenAmount;
  tokenValidity?: number;
  tokenAmountType?: TokenAmountType;
  paymentSchedule?: PaymentSchedule;
  policies?: PolicySet;
  distanceToCampus?: number;
  seatingCapacity?: string;
}

export interface CreatePaymentPlanOutput {
  id: string;
  termConfigId: string;
  pricingConfigId: string;
  tokenAmount?: TokenAmount;
  paymentUrl?: string;
  paymentFormData?: PaymentFormData;
  htmlForm?: string;
}

/**
 * V3 PaymentPlan — shape returned by the /payment-plans search endpoint.
 */
export interface PaymentPlanV3 {
  id: string;
  pricingConfigId: string;
  name?: string;
  planType: PaymentPlanType;
  status?: PaymentPlanStatus;
  partPaymentCount?: number | null;
  discount?: number;
  tokenValidityDays?: number;
  tokenAmountType?: TokenAmountType;
  tokenAmountFixed?: number;
  updatedAt?: number;
  installmentWeights?: Array<{ daysAfter: number; weight: number }> | null;
  token?: { amountFixed: number; amountType: TokenAmountType; validityDays: number } | null;
  policies?: {
    cancellationPolicy?: string | null;
    discountPolicy?: string | null;
    planChangePolicy?: string | null;
    refundPolicy?: string | null;
    tokenPolicy?: string | null;
  };
}

// ─── Subscription V2 input types (not generated by Smithy client) ─────────────

export interface PreviewSubscriptionV2Input {
  granteeId: string;
  paymentPlanId: string;
  startDate: string;
}

export interface CreateSubscriptionV2Input {
  granteeId: string;
  subscriberId: string;
  paymentPlanId: string;
  paymentId: string;
  startDate: string;
}

// ─── List / Search input types ────────────────────────────────────────────────

export interface ListSubscriptionsV2Input {
  index: SubscriptionV2Index;
  filter: string;
  ascending?: boolean;
  pageSize?: number;
  nextToken?: string;
}

export interface ListSubscriptionsV2Output {
  items: SubscriptionV2Summary[];
  nextToken?: string;
}

export interface SearchSubscriptionsV2Output {
  subscriptions: SubscriptionV2Summary[];
  total: number;
}

// ─── Re-export SubscriptionV2Index for consumers that import from here ────────
// (already covered by the type re-export block above)

// ─── API functions ────────────────────────────────────────────────────────────

export const createPayment = async (
  paymentData: CreatePaymentInput,
): Promise<CreatePaymentOutput> => {
  try {
    const response = await axiosInstance.post('/payments', paymentData);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const message = error.response.data?.message || 'Failed to create payment';
      throw new Error(message);
    }
    throw error;
  }
};

export const getPayment = async (paymentId: string): Promise<GetPaymentOutput> => {
  try {
    const response = await axiosInstance.get(`/payments/${paymentId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching payment:', error);
    throw error;
  }
};

/**
 * Search payment plans by pricing config ID.
 */
export async function searchPaymentPlans(pricingConfigId: string): Promise<PaymentPlanV3[]> {
  try {
    const query = `@pricingConfigId:{${pricingConfigId}}`;
    const response = await axiosInstance.get<{ paymentPlans: PaymentPlanV3[] }>(
      '/payment-plans/search',
      { params: { query } }
    );
    return (response.data.paymentPlans || []).filter(
      (plan: PaymentPlanV3) => plan.status !== PaymentPlanStatus.INACTIVE
    );
  } catch (error: any) {
    console.error('Error searching payment plans:', error);
    throw error;
  }
}

export const previewSubscription = async (
  input: PreviewSubscriptionV2Input,
): Promise<SubscriptionPreview> => {
  try {
    const response = await axiosInstance.post('/subscriptions-v2/preview', input);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const createSubscriptionV2 = async (
  input: CreateSubscriptionV2Input,
): Promise<CreateSubscriptionV2Output> => {
  const response = await axiosInstance.post('/subscriptions-v2', input);
  return response.data;
};

export const listSubscriptionV2 = async (
  input: ListSubscriptionsV2Input,
): Promise<ListSubscriptionsV2Output> => {
  try {
    const response = await axiosInstance.get(`/subscriptions-v2`, {
      params: {
        index: input.index,
        filter: input.filter,
        ascending: input.ascending,
        pageSize: input.pageSize,
        nextToken: input.nextToken,
      },
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Search subscriptions V2 by subscriberId.
 */
export const searchSubscriptionsV2 = async (
  subscriberId: string,
  limit = 100,
): Promise<SearchSubscriptionsV2Output> => {
  try {
    const query = `@subscriberId:{${subscriberId}} @status:{ACTIVE | PENDING_PAYMENT}`;
    const response = await axiosInstance.get<SearchSubscriptionsV2Output>(
      '/subscriptions-v2/search',
      { params: { query, limit } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error searching subscriptions V2:', error);
    throw error;
  }
};

/**
 * Search subscriptions V2 by granteeId (student ID).
 */
export const searchSubscriptionsByGranteeId = async (
  granteeId: string,
  limit = 10,
): Promise<SearchSubscriptionsV2Output> => {
  try {
    const query = `@granteeId:{${granteeId}}`;
    const response = await axiosInstance.get<SearchSubscriptionsV2Output>(
      '/subscriptions-v2/search',
      { params: { query, limit } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error searching subscriptions V2 by granteeId:', error);
    throw error;
  }
};

/**
 * Search subscriptions V2 by multiple granteeIds in a single query.
 * Uses Redis pipe syntax: @granteeId:{id1 | id2 | ...}
 */
export const searchSubscriptionsByGranteeIds = async (
  granteeIds: string[],
  limit = 100,
): Promise<SearchSubscriptionsV2Output> => {
  try {
    const query = `@granteeId:{${granteeIds.join(' | ')}} @status:{ACTIVE | PENDING_PAYMENT}`;
    const response = await axiosInstance.get<SearchSubscriptionsV2Output>(
      '/subscriptions-v2/search',
      { params: { query, limit } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error searching subscriptions V2 by granteeIds:', error);
    throw error;
  }
};

export const getSubscriptionV2 = async (
  subscriptionId: string,
): Promise<GetSubscriptionV2Output> => {
  try {
    const response = await axiosInstance.get(`/subscriptions-v2/${subscriptionId}`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Fetch all applicable transactions for a subscription
 * Includes: ADJUSTMENT, DISCOUNT, REFERRAL_COUPON categories
 * Returns only POSTED transactions that reduce the amount (CREDIT type)
 * Always uses "pending" status for unpaid amounts
 */
const TRANSACTION_CATEGORIES = ['ADJUSTMENT', 'DISCOUNT', 'REFERRAL_COUPON'] as const;
const TRANSACTION_STATUSES = ['PENDING', 'POSTED'] as const;

const normalizeSearchTransactions = (items: Transaction[]): Transaction[] =>
  items
    .filter(item => {
      const category = resolveTransactionType(item.transactionCategory);
      const status = resolveTransactionType(item.status);
      return (
        TRANSACTION_CATEGORIES.includes(category as (typeof TRANSACTION_CATEGORIES)[number]) &&
        TRANSACTION_STATUSES.includes(status as (typeof TRANSACTION_STATUSES)[number])
      );
    })
    .map(item => ({
      ...item,
      transactionType: resolveTransactionType(item.transactionType),
    }));

export const getSubscriptionTransactions = async (
  subscriptionId: string,
): Promise<Transaction[]> => {
  try {
    const query = `@linkedResourceId:{${subscriptionId}} @status:{PENDING | POSTED} @transactionCategory:{${TRANSACTION_CATEGORIES.join(' | ')}}`;

    const response = await axiosInstance.get('/transactions/search', {
      params: { query, offset: 0, limit: 100 },
    });
    const data = response.data as SearchTransactionsOutput;
    return normalizeSearchTransactions(data.items || []);
  } catch (error: any) {
    console.error('Error fetching subscription transactions:', error);
    return [];
  }
};

/**
 * Fetch specific transactions by their IDs using the search API.
 * Used on the Success page to resolve adjustments stamped on installments.
 */
export const getTransactionsByIds = async (ids: string[]): Promise<Transaction[]> => {
  if (ids.length === 0) return [];
  const unique = [...new Set(ids.filter(Boolean))];
  const results: Array<Transaction | null> = new Array(unique.length).fill(null);
  const workerCount = Math.min(8, unique.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < unique.length) {
      const index = nextIndex++;
      const id = unique[index];
      try {
        const response = await axiosInstance.get(`/transactions/${id}`);
        const item = response.data as Transaction;
        const hydrated: Transaction = {
          ...item,
          transactionType: resolveTransactionType(item.transactionType),
        };
        results[index] = hydrated;
      } catch {
        // A missing adjustment should not prevent the payment-success screen rendering.
      }
    }
  };
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results.filter((item): item is Transaction => item != null);
};

export interface Transaction {
  id: string;
  transactionCategory: string;
  transactionType?: string;
  /** Amount in rupees. Named 'amountMinor' for historical reasons; the API layer converts from paise before returning. */
  amountMinor: number;
  metadata?: Record<string, string>;
  linkedResource?: {
    resourceType: string;
    resourceId: string;
  };
  status: string;
  createdAt: number;
}

export interface SearchTransactionsOutput {
  items: Transaction[];
  total: number;
}

/** `ScheduledInstallment` extended with stamped adjustment transaction ID(s). */
export type InstallmentWithTxn = ScheduledInstallment & {
  transactionId?: string;
  transactionIds?: string[];
};

/** `IssuedToken` extended with stamped adjustment transaction ID(s). */
export type IssuedTokenWithTxn = IssuedToken & {
  transactionId?: string;
  transactionIds?: string[];
};

export type TxnStamp = {
  transactionId?: string;
  transactionIds?: string[];
};

/** Resolves stamped transaction IDs from a token/installment (legacy singular or list). */
export function resolveInstallmentTxnIds(record: TxnStamp | undefined | null): string[] {
  if (!record) return [];
  const fromList = [...new Set((record.transactionIds ?? []).filter(id => !!id))];
  if (fromList.length > 0) return fromList;
  if (record.transactionId) return [record.transactionId];
  return [];
}

export function collectStampedTxnIds(
  token?: TxnStamp | null,
  installments?: TxnStamp[] | null,
): string[] {
  const ids = new Set<string>();
  resolveInstallmentTxnIds(token).forEach(id => ids.add(id));
  (installments ?? []).forEach(inst => {
    resolveInstallmentTxnIds(inst).forEach(id => ids.add(id));
  });
  return Array.from(ids);
}

/**
 * Whole-and-sole CREDIT/DEBIT discriminator. Ignores metadata.adjustmentType.
 * Accepts a plain string or a Smithy `{ value: "DEBIT" }` enum object.
 */
export const resolveTransactionType = (raw: unknown): string => {
  if (typeof raw === 'string') return raw.toUpperCase();
  if (raw && typeof raw === 'object' && 'value' in raw) {
    return String((raw as { value?: unknown }).value ?? '').toUpperCase();
  }
  return '';
};

/**
 * `true` when the adjustment reduces the amount owed (CREDIT); `false` when it increases it (DEBIT).
 * Uses `transactionType` only — `metadata.adjustmentType` is not a source of truth.
 */
export const isCreditAdjustment = (
  txn: Pick<Transaction, 'transactionType'> & { transactionType?: unknown },
): boolean => resolveTransactionType(txn.transactionType) === 'CREDIT';

/** PayU / backend guardrail: charge must stay ≥ ₹1 after adjustments. */
export const MIN_PAYABLE_RUPEES = 1;

/** Round to paise (2 dp). Prefer over Math.round so display and PayU wire amounts agree. */
export const roundToPaise = (amount: number): number =>
  Math.round(amount * 100) / 100;

/** Signed rupee delta for one adjustment (CREDIT negative, DEBIT positive). */
export const adjustmentSignedAmount = (txn: Transaction): number => {
  const amount = Number(txn.amountMinor) || 0;
  return isCreditAdjustment(txn) ? -amount : amount;
};

/**
 * Net adjustment for a list of transactions.
 * CREDIT reduces the amount owed; DEBIT increases it.
 * Sums first, then rounds once to paise (not per-item, not to whole rupees).
 */
export const calculateTotalAdjustment = (adjustments: Transaction[]): number => {
  const total = adjustments.reduce((sum, adj) => {
    const adjAmount = Number(adj.amountMinor) || 0;
    return isCreditAdjustment(adj) ? sum - adjAmount : sum + adjAmount;
  }, 0);
  return roundToPaise(total);
};

export type AdjustmentSelection = {
  selected: Transaction[];
  deferred: Transaction[];
  netAdjustment: number;
  payableAmount: number;
};

/**
 * Select pending adjustments that fit under the ₹1 floor without forfeiting credit value.
 *
 * Debits first (they raise headroom), then greedy FIFO by createdAt within each group.
 * A credit that would push the charge below [minPayable] is deferred (stays PENDING
 * for a later payment) instead of being stamped while Math.max(1, …) silently eats
 * the overflow. Final payable is floored at [minPayable] so a zero/sub-rupee base
 * with no adjustments still meets the PayU minimum.
 *
 * Mirrors the server guardrail in PaymentAccessor.applyAdjustmentsToAmount.
 */
export const selectAdjustmentsForPayment = (
  baseAmount: number,
  pending: Transaction[],
  minPayable: number = MIN_PAYABLE_RUPEES,
): AdjustmentSelection => {
  const sorted = [...pending].sort((a, b) => {
    // Debits before credits so a later debit can make room for an earlier credit.
    const byKind = Number(isCreditAdjustment(a)) - Number(isCreditAdjustment(b));
    if (byKind !== 0) return byKind;
    return (a.createdAt ?? 0) - (b.createdAt ?? 0);
  });

  const selected: Transaction[] = [];
  const deferred: Transaction[] = [];
  let net = 0;

  for (const txn of sorted) {
    const delta = adjustmentSignedAmount(txn);
    if (baseAmount + net + delta >= minPayable) {
      selected.push(txn);
      net += delta;
    } else {
      deferred.push(txn);
    }
  }

  const payableAmount = Math.max(minPayable, roundToPaise(baseAmount + net));
  return {
    selected,
    deferred,
    netAdjustment: roundToPaise(net),
    payableAmount,
  };
};

/** Format a payable amount for CreatePayment (2 dp string; preserves paise on the wire). */
export const formatPayableAmount = (amount: number): string =>
  roundToPaise(amount).toFixed(2);
